import type { Call, Device } from "@twilio/voice-sdk";
import {
    addCallContextParameter,
    checkRequiredParameter,
    checkWorker,
    getConferenceRecordingConfiguration,
    getDefaultCallerID,
    getDefaultQueueSid,
    getDefaultWorkflowSid,
    hasVoiceTaskWithStatus,
    isOutboundCallingEnabled,
    isWorkerOffline
} from "../../ActionUtils";
import { ConferenceOptions, Reservation, ReservationEvents, Worker } from "~/packages/taskrouter";
import type { VoiceCall } from "~/modules/VoiceCall";
import { VoiceCallImpl } from "~/modules/VoiceCall";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { AccountConfigData } from "~/backend/generated/Configuration/model/accountConfigData";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { getConfigRegion } from "~/utils/regionUtil";
import { createFlexSdkError, throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface StartOutboundCallOptions {
    fromNumber?: string;
    workflowSid?: string;
    taskQueueSid?: string;
    attributesForTaskCreation?: object;
    conferenceOptions?: Partial<ConferenceOptions>;
    voiceDevice?: Device;
    reservationEventListeners?: Partial<ReservationEvents>;
}





















































export class StartOutboundCall implements Action<Promise<VoiceCall>> {
    


    readonly #toNumber: string;

    


    readonly #options?: StartOutboundCallOptions;

    


    readonly #defaultConferenceOptions: ConferenceOptions = {
        endConferenceOnCustomerExit: true,
        endConferenceOnExit: true
    };

    constructor(toNumber: string, options?: StartOutboundCallOptions) {
        this.#toNumber = toNumber;
        if (options) {
            this.#options = {
                ...options,
                conferenceOptions: {
                    ...this.#defaultConferenceOptions,
                    ...options.conferenceOptions
                }
            };
        } else {
            this.#options = {
                conferenceOptions: this.#defaultConferenceOptions
            };
        }
    }

    async run(ctx: {}): Promise<VoiceCall> {
        const context = ctx as ContextManager;

        const session = context.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        const flexSessionIdentifier = session.flexSessionIdentifier;

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)("StartOutboundCall");
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const accountConfig = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );
        const clientOptions = context.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        const voiceController = context.retrieveInstanceOf(ContextInstance.VoiceControllerImpl, VoiceControllerImpl);

        logger.debug(`StartOutboundCall invoked to number: ${this.#toNumber}`);

        const worker = taskRouter.worker;
        const { attributesForTaskCreation, conferenceOptions, fromNumber, taskQueueSid, workflowSid } =
            this.#options ?? {};

        checkWorker(worker, {
            actionName: "StartOutboundCall",
            moduleName: "actions/Voice",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.StartOutboundCallCompleted,
            extra: {
                taskQueueSid,
                workflowSid
            }
        });

        checkRequiredParameter(this.#toNumber, "toNumber", {
            actionName: "StartOutboundCall",
            moduleName: "actions/Voice",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.StartOutboundCallCompleted,
            extra: {
                taskQueueSid,
                workflowSid
            }
        });

        const accountConfigContainer = accountConfig.get();
        const fromNumberParam = fromNumber ?? getDefaultCallerID(accountConfigContainer.outboundCallFlows);
        if (!fromNumberParam) {
            const errorMsg = "StartOutboundCall: Parameter 'fromNumber' could not be found";
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, { error: errorMsg });
            return throwFlexSdkError(context)(
                ErrorCode.FromNumberRequired,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        const taskQueueSidParam = taskQueueSid ?? getDefaultQueueSid(accountConfigContainer.outboundCallFlows);
        if (!taskQueueSidParam) {
            const errorMsg = "StartOutboundCall: Task queue SID is required";
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber
            });
            return throwFlexSdkError(context)(
                ErrorCode.TaskQueueSidRequired,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        const workflowSidParam = workflowSid ?? getDefaultWorkflowSid(accountConfigContainer.outboundCallFlows);
        if (!workflowSidParam) {
            const errorMsg = "StartOutboundCall: Workflow SID is required";
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid
            });
            return throwFlexSdkError(context)(
                ErrorCode.WorkflowSidRequired,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        const taskrouterOfflineActivitySid = accountConfigContainer.taskrouterOfflineActivitySid;
        if (!taskrouterOfflineActivitySid) {
            const errorMsg = "StartOutboundCall: taskrouterOfflineActivitySid is undefined in account configuration";
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throwFlexSdkError(context)(
                ErrorCode.TaskrouterOfflineActivitySidNotConfigured,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        if (isWorkerOffline(worker, taskrouterOfflineActivitySid)) {
            const errorMsg = `StartOutboundCall: Worker is offline, outbound call cancelled`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throwFlexSdkError(context)(
                ErrorCode.WorkerOfflineOutboundCallCancelled,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        if (!isOutboundCallingEnabled(accountConfigContainer.outboundCallFlows)) {
            const errorMsg = `StartOutboundCall: Outbound calling is disabled in Flex account configuration, outbound call cancelled`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throwFlexSdkError(context)(ErrorCode.OutboundCallingDisabled, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (hasVoiceTaskWithStatus(worker, "pending")) {
            const errorMsg = `StartOutboundCall: Another voice task is already in pending status, outbound call cancelled`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throwFlexSdkError(context)(
                ErrorCode.InboundCallPendingOutboundCancelled,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        if (hasVoiceTaskWithStatus(worker, "accepted")) {
            const errorMsg = `StartOutboundCall: Another voice task is already in accepted status, outbound call cancelled`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throwFlexSdkError(context)(
                ErrorCode.InboundCallAcceptedOutboundCancelled,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        const voiceDevice = this.#options?.voiceDevice;
        await voiceController.init(voiceDevice);

        if (!voiceController.isAudioInputDeviceAvailable()) {
            const errorMsg = `StartOutboundCall: No audio input device, outbound call cancelled`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid,
                customVoiceDevice: !!voiceDevice
            });
            throwFlexSdkError(context)(
                ErrorCode.NoAudioInputDeviceAvailable,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        return new Promise((resolve, reject) => {
            let taskSid: string;
            
            let cleanup: (() => void) | undefined;
            let currentReservation: Reservation;
            let handleReservationAccepted: (reservation: Reservation) => void;

            const handlerContext = {
                resolve,
                reject,
                contextManager: context,
                logger,
                analytics,
                worker,
                voiceController,
                voiceDevice,
                fromNumberParam,
                taskQueueSidParam,
                workflowSidParam,
                flexSessionIdentifier,
                taskRouter,
                accountConfigContainer: accountConfigContainer,
                clientOptions,
                conferenceOptions,
                getCurrentTaskSid: () => taskSid,
                setTaskSid: (newTaskSid: string) => {
                    taskSid = newTaskSid;
                },
                getCurrentReservation: () => currentReservation,
                setCurrentReservation: (reservation: Reservation) => {
                    currentReservation = reservation;
                },
                getReservationAcceptedHandler: () => handleReservationAccepted,
                setReservationAcceptedHandler: (handler: (reservation: Reservation) => void) => {
                    handleReservationAccepted = handler;
                },
                getCleanup: () => cleanup
            };

            const { handleIncomingCall, handleReservationCreated, handleReservationFailed } =
                this.#defineHandlers(handlerContext);
            const timeoutId = setTimeout(() => {
                const errorMsg = `StartOutboundCall: Timeout occurred because no incoming call event was received within 30 seconds`;
                logger.error(errorMsg);
                sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid,
                    fromNumber,
                    taskQueueSid,
                    workflowSid,
                    customVoiceDevice: !!voiceDevice
                });
                reject(
                    createFlexSdkError(context)(
                        ErrorCode.IncomingCallTimeout,
                        { severity: ErrorSeverity.Error },
                        errorMsg
                    )
                );
                cleanup?.();
            }, 30_000);

            cleanup = () => {
                clearTimeout(timeoutId);
                voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);
                worker.off("reservationCreated", handleReservationCreated);
                worker.off("reservationFailed", handleReservationFailed);
                currentReservation?.off("accepted", handleReservationAccepted);
            };

            voiceController.subscribeToIncomingCallEvent(handleIncomingCall);
            worker.on("reservationCreated", handleReservationCreated);
            worker.on("reservationFailed", handleReservationFailed);

            void (async () => {
                try {
                    const outboundAttributes = {
                        ...attributesForTaskCreation,
                        direction: "outbound",
                        flex_session_identifier_task_created: flexSessionIdentifier
                    };
                    const taskOptions = {
                        taskChannelUniqueName: "voice",
                        attributes: outboundAttributes
                    };

                    taskSid = await worker.createTask(
                        this.#toNumber,
                        fromNumberParam,
                        workflowSidParam,
                        taskQueueSidParam,
                        taskOptions
                    );
                } catch (error) {
                    cleanup();
                    const errorMsg = `StartOutboundCall: Failed to create task`;
                    sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                        error: `${errorMsg} (${toCauseMessage(error)})`,
                        fromNumber,
                        taskQueueSid,
                        workflowSid,
                        customVoiceDevice: !!voiceDevice
                    });
                    logger.error(errorMsg, error);
                    reject(
                        createFlexSdkError(context)(
                            ErrorCode.FailedToCreateTask,
                            { severity: ErrorSeverity.Error },
                            errorMsg,
                            error as Error
                        )
                    );
                }
            })();
        });
    }

    #addReservationEventListeners(reservation: Reservation): void {
        if (this.#options?.reservationEventListeners) {
            Object.entries(this.#options.reservationEventListeners).forEach(([event, handler]) => {
                if (handler) {
                    reservation.on(event as keyof ReservationEvents, handler);
                }
            });
        }
    }

    #defineHandlers(handlerContext: {
        resolve: (value: VoiceCall) => void;
        reject: (reason?: Error) => void;
        contextManager: ContextManager;
        logger: ReturnType<ReturnType<typeof getLogger>>;
        analytics: AnalyticsImpl;
        worker: Worker;
        voiceController: VoiceControllerImpl;
        voiceDevice?: Device;
        fromNumberParam: string;
        taskQueueSidParam: string;
        workflowSidParam: string;
        flexSessionIdentifier: string;
        taskRouter: TaskRouterImpl;
        accountConfigContainer: AccountConfigData;
        clientOptions: ClientOptionsStore;
        conferenceOptions?: Partial<ConferenceOptions>;
        getCurrentTaskSid: () => string;
        setTaskSid: (taskSid: string) => void;
        getCurrentReservation: () => Reservation;
        setCurrentReservation: (reservation: Reservation) => void;
        getReservationAcceptedHandler: () => (reservation: Reservation) => void;
        setReservationAcceptedHandler: (handler: (reservation: Reservation) => void) => void;
        getCleanup: () => (() => void) | undefined;
    }) {
        const {
            resolve,
            reject,
            contextManager,
            logger,
            analytics,
            worker,
            voiceController,
            voiceDevice,
            fromNumberParam,
            taskQueueSidParam,
            workflowSidParam,
            flexSessionIdentifier,
            taskRouter,
            accountConfigContainer,
            clientOptions,
            conferenceOptions,
            getCurrentTaskSid,
            setCurrentReservation,
            setReservationAcceptedHandler,
            getCleanup
        } = handlerContext;

        const handleIncomingCall = (call: Call) => {
            logger.info(
                "Incoming call event received in StartOutboundCall",
                call?.parameters?.From,
                call?.parameters?.To
            );
            if (call?.parameters?.From === fromNumberParam) {
                logger.debug("From phone numbers match");

                const cleanup = getCleanup();
                cleanup?.();
                const voiceCall = new VoiceCallImpl(contextManager, call, voiceController.voiceDevice);

                sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                    taskSid: getCurrentTaskSid(),
                    fromNumber: fromNumberParam,
                    taskQueueSid: taskQueueSidParam,
                    workflowSid: workflowSidParam,
                    customVoiceDevice: !!voiceDevice,
                    callSid: call.parameters?.CallSid
                });
                resolve(voiceCall);
            } else {
                logger.error(
                    "Incoming call is coming from a different number, not from the one given as an argument to StartOutboundCall action"
                );
            }
        };

        
        let handleReservationFailed: (reservation: Reservation) => void;

        const handleReservationCreated = (reservation: Reservation) => {
            void (async () => {
                logger.info("A reservation has been created for the task", reservation?.task?.sid);
                setCurrentReservation(reservation);
                this.#addReservationEventListeners(reservation);
                if (reservation.task.sid === getCurrentTaskSid()) {
                    try {
                        const to = addCallContextParameter(
                            `flex_session_identifier=${flexSessionIdentifier}`,
                            taskRouter.worker.attributes.contact_uri as string,
                            conferenceOptions?.to
                        );

                        const effectiveConferenceOptions = {
                            ...conferenceOptions,
                            to,
                            ...getConferenceRecordingConfiguration(
                                accountConfigContainer,
                                getConfigRegion(clientOptions),
                                getCurrentTaskSid(),
                                conferenceOptions
                            )
                        };

                        const conferenceCreated = await reservation.conference(effectiveConferenceOptions);
                        logger.debug("Conference created", conferenceCreated);

                        const handleReservationAccepted = () => {
                            if (!voiceController.voiceDevice.isBusy) {
                                logger.info("Call was accepted in another device");
                                sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                                    taskSid: getCurrentTaskSid(),
                                    fromNumber: fromNumberParam,
                                    taskQueueSid: taskQueueSidParam,
                                    workflowSid: workflowSidParam,
                                    customVoiceDevice: !!voiceDevice,
                                    callAcceptedInAnotherDevice: true
                                });
                                resolve(new VoiceCallImpl(contextManager, null, voiceController.voiceDevice));
                                const cleanup = getCleanup();
                                cleanup?.();
                            }
                        };
                        setReservationAcceptedHandler(handleReservationAccepted);
                        reservation.on("accepted", handleReservationAccepted);
                    } catch (error) {
                        const errorMsg = `StartOutboundCall: Error creating conference for reservation ${reservation.sid}`;
                        logger.error(errorMsg, error);
                        sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                            error: `${errorMsg} (${toCauseMessage(error)})`,
                            taskSid: getCurrentTaskSid(),
                            fromNumber: fromNumberParam,
                            taskQueueSid: taskQueueSidParam,
                            workflowSid: workflowSidParam,
                            customVoiceDevice: !!voiceDevice
                        });
                        reject(
                            createFlexSdkError(contextManager)(
                                ErrorCode.FailedToCreateConference,
                                { severity: ErrorSeverity.Error },
                                errorMsg,
                                error as Error
                            )
                        );
                        const cleanup = getCleanup();
                        cleanup?.();
                    } finally {
                        worker.off("reservationCreated", handleReservationCreated);
                        worker.off("reservationFailed", handleReservationFailed);
                    }
                }
            })();
        };

        handleReservationFailed = (reservation: Reservation) => {
            setCurrentReservation(reservation);
            logger.error("Failed to create reservation for task:", reservation?.task?.sid);
            if (reservation.task.sid === getCurrentTaskSid()) {
                const errorMsg = `StartOutboundCall: Failed to create reservation for outbound call task ${reservation.task.sid}`;
                logger.error(errorMsg);
                sendWorkerTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: getCurrentTaskSid(),
                    fromNumber: fromNumberParam,
                    taskQueueSid: taskQueueSidParam,
                    workflowSid: workflowSidParam,
                    customVoiceDevice: !!voiceDevice
                });
                reject(
                    createFlexSdkError(contextManager)(
                        ErrorCode.FailedToCreateVoiceTaskReservation,
                        { severity: ErrorSeverity.Error },
                        errorMsg
                    )
                );
                const cleanup = getCleanup();
                cleanup?.();
            }
        };

        return { handleIncomingCall, handleReservationCreated, handleReservationFailed };
    }
}
